import type { DscApplicantSection } from "@credit-acceptance/step6-bff-client";
import { useTranslation } from "react-i18next";

import { Form } from "../../../../components/ui/form";
import { useScrollSpy } from "../../../../hooks/useScrollSpy";
import { useReadOnly } from "../../../lock/ReadOnlyContext";
import type { ScrollSpySection } from "../../../stipulations/stipulationCategoryFilters";
import { useStipulationCategoryStore } from "../../../stipulations/stipulationCategoryStore";
import Section from "../../components/Section";
import { SignerColumn } from "./fields/SignerColumn";
import { ReadOnlyProvider } from "./readOnlyContext";
import { useSignersForm } from "./useSignersForm";

// ---------------------------------------------------------------------------
// Signers card — collapsible outer card (via Section/Accordion) with a
// two-column Driver | 2nd Signer layout on desktop, stacked on mobile.
//
// Visibility is fully driven by the DSC Workbench dashboard response: the
// Driver column renders only when `driver.present` is true, and the
// 2nd Signer column only when `secondSigner.present` is true. There is
// no add/remove-2nd-signer flow — the response is the single source of truth
// for who appears on the deal.
//
// Form VALUES are still local-only (BFF income hydration is deferred). Each
// present signer seeds exactly ONE blank income block on mount; totals show
// $0.00 until income hydration is added in a future ticket. The GET response
// (step6-bff-client 1.0.171) no longer carries income or employment data.
// ---------------------------------------------------------------------------
interface SignersProperties {
  /** Primary applicant (driver) section from the dashboard response. */
  driver?: DscApplicantSection;
  /** Second signer (cosigner) section from the dashboard response. */
  secondSigner?: DscApplicantSection;
  /** Deal / approval id, threaded down to each income block's employer-info flow. */
  approvalId?: string;
}

function Signers({ driver, secondSigner, approvalId }: SignersProperties) {
  const { t } = useTranslation();
  const readOnly = useReadOnly();
  const showDriver = Boolean(driver?.present);
  const showSecondSigner = Boolean(secondSigner?.present);

  const { form, control, driverIncomes, secondSignerIncomes, driverSsnType, secondSignerSsnType } = useSignersForm({
    driver,
    secondSigner,
  });

  // Real applicant ids for the Work Number flow, sourced straight from the
  // dashboard response (driver/secondSigner.applicantId).
  const driverApplicantId = driver?.applicantId;
  const secondSignerApplicantId = secondSigner?.applicantId;

  // Scroll-spy: as the analyst scrolls past each Income / Bank / Residence
  // subsection (in either column), drive the Stipulations panel's category
  // filter to match. Called unconditionally — before the early return below —
  // per the rules of hooks.
  const setStipulationCategory = useStipulationCategoryStore((state) => state.setCategory);
  const registerSection = useScrollSpy<ScrollSpySection>(setStipulationCategory);

  // Nothing to render until the response includes at least one present signer.
  if (!showDriver && !showSecondSigner) {
    return null;
  }

  return (
    <Section title={t("signers.sectionTitle")}>
      <Form {...form}>
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <form
          onSubmit={form.handleSubmit(() => {
            /* TODO: wire to BFF mutation */
          })}
          noValidate
        >
          {/* A disabled fieldset disables every nested *native* control, so the
              whole Signers form goes read-only under the VLA lock. Radix Select
              triggers and the tel: call anchor ignore the fieldset, so they read
              the same flag from ReadOnlyProvider instead. */}
          <ReadOnlyProvider value={readOnly}>
            <fieldset disabled={readOnly} className="m-0 w-full min-w-0 border-0 p-0">
              {/* Two-column layout: Driver | 2nd Signer */}
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {showDriver && (
                  <SignerColumn
                    title={t("signers.driver")}
                    personPath="driver"
                    control={control}
                    fields={driverIncomes.fields}
                    append={driverIncomes.append}
                    remove={driverIncomes.remove}
                    approvalId={approvalId}
                    applicantId={driverApplicantId}
                    applicantSsnType={driverSsnType}
                    registerSection={registerSection}
                  />
                )}

                {showSecondSigner && (
                  // When both columns render, separate them with a divider:
                  // horizontal above the 2nd Signer when stacked, vertical between
                  // the columns at lg. No divider when the 2nd Signer stands alone.
                  <div
                    className={
                      showDriver ? "border-border border-t pt-6 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0" : undefined
                    }
                  >
                    <SignerColumn
                      title={t("signers.secondSigner")}
                      personPath="secondSigner"
                      control={control}
                      fields={secondSignerIncomes.fields}
                      append={secondSignerIncomes.append}
                      remove={secondSignerIncomes.remove}
                      approvalId={approvalId}
                      applicantId={secondSignerApplicantId}
                      applicantSsnType={secondSignerSsnType}
                      registerSection={registerSection}
                    />
                  </div>
                )}
              </div>
            </fieldset>
          </ReadOnlyProvider>
        </form>
      </Form>
    </Section>
  );
}

export default Signers;
